import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { fetchSpeakingExercise, saveExerciseProgress, loadExerciseProgress, resetExerciseProgress, recordExerciseAttempt } from '../../services/api';
import { useExerciseTimer } from '../../hooks/useExerciseTimer';
import Loading from '../Loading';
import Error from '../Error';
import SpeakingPanel from './SpeakingPanel';
import Results from './Results';
import ExerciseLayout from '../shared/ExerciseLayout';

const SpeakingExercise = forwardRef(({ topic = 'groceries', onBack }, ref) => {
  const [instruction, setInstruction] = useState('');
  const [questions, setQuestions] = useState([]);
  const [userAnswers, setUserAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const { stop: stopTimer } = useExerciseTimer();

  // Expose saveProgress method to parent component
  useImperativeHandle(ref, () => ({
    saveProgress: async () => {
      try {
        await saveExerciseProgress(topic, 'speaking', {
          userAnswers
        });
      } catch (err) {
        console.error('Error saving progress from ref:', err);
        throw err;
      }
    }
  }));

  // Fetch exercises from API on component mount
  useEffect(() => {
    const loadExercises = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await fetchSpeakingExercise({ topic });

        setInstruction(data.instruction);
        setQuestions(data.questions);

      } catch (err) {
        console.error('Error fetching speaking exercises:', err);
        setError(err.message || 'Failed to load exercises');
      } finally {
        setLoading(false);
      }
    };

    loadExercises();
  }, [topic]);

  // Load saved progress after questions are loaded
  useEffect(() => {
    const loadProgress = async () => {
      if (!loading && questions.length > 0) {
        try {
          const savedProgress = await loadExerciseProgress(topic, 'speaking');
          if (savedProgress && savedProgress.progress_data) {
            if (savedProgress.progress_data.userAnswers) {
              setUserAnswers(savedProgress.progress_data.userAnswers);
            }
          }
        } catch (err) {
          console.error('Error loading progress:', err);
        }
      }
    };

    loadProgress();
  }, [loading, questions.length, topic]);

  const allAnswered = questions.every(q => userAnswers[q.id]);

  const handleMarkAsAnswered = async (questionId) => {
    const updatedAnswers = {
      ...userAnswers,
      [questionId]: true
    };
    setUserAnswers(updatedAnswers);

    // Save progress
    try {
      await saveExerciseProgress(topic, 'speaking', {
        userAnswers: updatedAnswers
      });
    } catch (err) {
      console.error('Error saving progress:', err);
    }
  };

  const handleRemoveAnswer = async (questionId) => {
    const newAnswers = { ...userAnswers };
    delete newAnswers[questionId];
    setUserAnswers(newAnswers);

    // Save progress
    try {
      await saveExerciseProgress(topic, 'speaking', {
        userAnswers: newAnswers
      });
    } catch (err) {
      console.error('Error saving progress:', err);
    }
  };

  const submitAnswers = async () => {
    // Stop timer and get final time
    const finalTime = stopTimer();

    // Speaking exercise has no "error" concept - all answered = perfect
    // Score is the number of questions answered
    const answeredCount = Object.keys(userAnswers).length;

    // Record exercise attempt (no errors since speaking is self-assessment)
    try {
      await recordExerciseAttempt({
        topic: topic,
        exercise_type: 'speaking',
        score: answeredCount,
        max_score: questions.length,
        time_spent_seconds: finalTime,
        errors: null  // Speaking exercises have no errors
      });
    } catch (trackingError) {
      console.error('Failed to record exercise attempt:', trackingError);
    }

    // Reset progress after completion
    try {
      await resetExerciseProgress(topic, 'speaking');
    } catch (err) {
      console.error('Error resetting progress on submit:', err);
    }

    setShowResults(true);
  };

  const handleRetry = async () => {
    setUserAnswers({});
    setShowResults(false);
    try {
      await resetExerciseProgress(topic, 'speaking');
    } catch (err) {
      console.error('Error resetting progress on retry:', err);
    }
  };

  if (loading) return <Loading />;
  if (error) return <Error message={error} />;

  if (showResults) {
    return (
      <Results
        questions={questions}
        userAnswers={userAnswers}
        onRetry={handleRetry}
        instruction={instruction}
        onBack={onBack}
        topic={topic}
      />
    );
  }

  return (
    <ExerciseLayout
      title="Mluveni"
      subtitle={instruction}
      color="blue"
      icon="💬"
      progress={{ current: Object.keys(userAnswers).length, total: questions.length }}
      onBack={onBack}
    >
      <SpeakingPanel
        questions={questions}
        userAnswers={userAnswers}
        onMarkAsAnswered={handleMarkAsAnswered}
        onRemoveAnswer={handleRemoveAnswer}
        onSubmit={submitAnswers}
        allAnswered={allAnswered}
        topic={topic}
      />
    </ExerciseLayout>
  );
});

export default SpeakingExercise;
